/* Aria Robotics — statische Inhalte (DE). Dienen als Fallback, falls
 * PocketBase nicht erreichbar ist (Blog/News). Ported aus data.jsx. */

export interface ContactInfo {
  company: string;
  street: string;
  city: string;
  country: string;
  email: string;
  press: string;
  phone: string;
  hours: string;
}
export interface IconTextItem { icon: string; h: string; p: string; }
export interface Spec { k: string; v: string; }
export interface GalleryItem { img: string; room: string; h: string; p: string; }
export interface NewsItem { slug: string; img?: string; date: string; tag: string; title: string; teaser: string; }
export interface BlogItem { slug: string; img: string; date: string; read: string; tag: string; title: string; teaser: string; }
export interface TeamMember { name: string; role: string; bio: string; }
export interface Milestone { year: string; t: string; d: string; }

export interface AriaContent {
  contact: ContactInfo;
  values: IconTextItem[];
  specs: Spec[];
  capabilities: IconTextItem[];
  gallery: GalleryItem[];
  limits: string[];
  serviceItems: IconTextItem[];
  news: NewsItem[];
  blog: BlogItem[];
  team: TeamMember[];
  milestones: Milestone[];
}

export const AriaData: AriaContent = {
  contact: {
    company: 'Aria Robotics GmbH',
    street: 'Spinnereistraße 7',
    city: '04179 Leipzig',
    country: 'Deutschland',
    email: 'hallo@aria-robotics.xyz',
    press: 'presse@aria-robotics.xyz',
    phone: '+49 341 5550 120',
    hours: 'Mo–Fr, 9–17 Uhr',
  },

  values: [
    { icon: 'clock', h: 'Zeit zurückgeben', p: 'Im Schnitt rund 12 Stunden pro Woche weniger Hausarbeit. Zeit, die Ihnen gehört.' },
    { icon: 'shield-check', h: 'Vertrauen durch Transparenz', p: 'Jede Aktion ist im Verlauf einsehbar. Wir benennen ehrlich, was Nestor kann — und was noch nicht.' },
    { icon: 'heart-handshake', h: 'Fürsorge mit Substanz', p: 'Echter Alltagsnutzen statt Show. Unterstützung, die niemanden bevormundet.' },
    { icon: 'home', h: 'Würde im Alltag', p: 'Nestor ist Gast in Ihrem Zuhause. Er fragt, bevor er handelt, und lässt die Entscheidung bei Ihnen.' },
  ],

  specs: [
    { k: 'Höhe', v: '1,62 m' },
    { k: 'Gewicht', v: '48 kg' },
    { k: 'Akkulaufzeit', v: 'bis 9 Std. aktiv' },
    { k: 'Ladezeit', v: '2,5 Std. an der Station' },
    { k: 'Traglast', v: 'bis 8 kg pro Arm' },
    { k: 'Sensorik', v: '6 Kameras · Tiefensensor · Mikrofonarray' },
    { k: 'Sprachen', v: 'Deutsch · Englisch' },
    { k: 'Geräuschpegel', v: 'unter 42 dB' },
    { k: 'Konnektivität', v: 'WLAN · Matter · Thread' },
    { k: 'Datenschutz', v: 'lokale Verarbeitung · Kamera-Abdeckung' },
  ],

  capabilities: [
    { icon: 'utensils', h: 'In der Küche', p: 'Tisch decken und abräumen, Geschirrspüler ein- und ausräumen, sogar Pasta aufsetzen.' },
    { icon: 'shirt', h: 'Wäsche & Betten', p: 'Wäsche sortieren und zusammenlegen, Betten frisch beziehen und glatt ziehen.' },
    { icon: 'sparkles', h: 'Ordnung', p: 'Spielzeug aufsammeln, Fenster putzen und aufräumen — nach Ihren Vorlieben.' },
    { icon: 'trees', h: 'Im Garten', p: 'Rasen mähen, Laub harken und Pflanzen gießen. Nestor hilft auch draußen.' },
  ],

  gallery: [
    { img: 'assets/nestor/pasta.webp', room: 'Küche', h: 'Pasta kochen', p: 'Wasser aufsetzen, Nudeln timen, abgießen — Schritt für Schritt.' },
    { img: 'assets/nestor/tisch.webp', room: 'Esszimmer', h: 'Tisch abräumen', p: 'Geschirr einsammeln, Krümel weg, Tisch wieder einladend.' },
    { img: 'assets/nestor/fenster.webp', room: 'Wohnzimmer', h: 'Fenster putzen', p: 'Streifenfrei bis in die Ecken, ganz ohne Leiter-Akrobatik von Ihnen.' },
    { img: 'assets/nestor/bett.webp', room: 'Schlafzimmer', h: 'Bett machen', p: 'Laken glatt ziehen, Decke aufschütteln, Kissen richten.' },
    { img: 'assets/nestor/lego.webp', room: 'Wohnzimmer', h: 'Spielzeug aufsammeln', p: 'Am Abend liegt kein Legostein mehr im Weg.' },
    { img: 'assets/nestor/giessen.webp', room: 'Garten', h: 'Pflanzen gießen', p: 'Regelmäßig und dosiert — auch wenn Sie unterwegs sind.' },
    { img: 'assets/nestor/rasen.webp', room: 'Garten', h: 'Rasen mähen', p: 'Gleichmäßige Bahnen, leise und sicher rund um die Terrasse.' },
    { img: 'assets/nestor/blaetter.webp', room: 'Garten', h: 'Laub harken', p: 'Im Herbst hält Nestor Wege und Rasen frei.' },
  ],

  limits: [
    'Treppen mit mehr als zwei Stufen steigt Nestor noch nicht sicher.',
    'Nestor kocht nicht mit offenem Feuer und hantiert nicht mit scharfen Klingen.',
    'Nestor ist kein Ersatz für Pflege oder Aufsicht von Kindern.',
    'Bei Unsicherheit hält Nestor inne und fragt nach, statt zu raten.',
  ],

  serviceItems: [
    { icon: 'package-open', h: 'Einrichtung zuhause', p: 'Ein Aria-Techniker richtet Nestor bei Ihnen ein und geht mit Ihnen die ersten Aufgaben durch. In etwa 90 Minuten ist alles startklar.' },
    { icon: 'refresh-cw', h: 'Software-Updates', p: 'Neue Fähigkeiten kommen automatisch und kostenfrei. Sie entscheiden, wann installiert wird — nie ungefragt.' },
    { icon: 'wrench', h: 'Wartung & Reparatur', p: 'Jährliche Inspektion inklusive. Ersatzteile aus Leipzig, meist innerhalb von 48 Stunden.' },
    { icon: 'life-buoy', h: 'Persönlicher Support', p: 'Ein Ansprechpartner statt Warteschleife — telefonisch, per Chat oder Video, Mo–Fr von 9 bis 17 Uhr.' },
    { icon: 'shield', h: 'Garantie', p: 'Drei Jahre Garantie auf Nestor 01. Verschleißteile transparent aufgeführt, ohne Kleingedrucktes.' },
    { icon: 'recycle', h: 'Rücknahme', p: 'Am Ende der Nutzung nehmen wir Nestor zurück und bereiten Komponenten für ein zweites Leben auf.' },
  ],

  news: [
    { slug: 'auslieferung-welle-1', img: 'assets/nestor/bett.webp', date: '2026-06-18', tag: 'Produkt', title: 'Nestor 01 startet in die erste Auslieferungswelle', teaser: 'Die ersten Haushalte in Leipzig, Berlin und München erhalten ihren Nestor. Wir liefern bewusst langsam — jede Einrichtung wird persönlich begleitet.' },
    { slug: 'serie-b', date: '2026-04-09', tag: 'Unternehmen', title: 'Aria Robotics schließt Serie-B-Finanzierung ab', teaser: '48 Mio. Euro fließen in Fertigung und Sicherheit. Kein Wachstum um jeden Preis — wir bauen Nestor so sorgfältig, wie man ihn ins eigene Zuhause lässt.' },
    { slug: 'werk-leipzig', img: 'assets/nestor/ansichten.webp', date: '2026-02-24', tag: 'Unternehmen', title: 'Neues Montagewerk in Leipzig eröffnet', teaser: 'Auf 6.000 m² fertigen wir Nestor künftig in Deutschland — kurze Wege, faire Arbeit, reparierbare Technik.' },
    { slug: 'tuev-zertifizierung', img: 'assets/nestor/rasen.webp', date: '2026-01-15', tag: 'Sicherheit', title: 'TÜV-Zertifizierung für Heimassistenz erhalten', teaser: 'Nestor 01 erfüllt die geprüften Sicherheitsanforderungen für den Einsatz im privaten Wohnraum.' },
  ],

  blog: [
    { slug: 'wie-viel-hausarbeit', img: 'assets/nestor/lego.webp', date: '2026-05-30', read: '6 Min.', tag: 'Ratgeber', title: 'Wie viel Hausarbeit nimmt Nestor 01 wirklich ab?', teaser: 'Ehrliche Zahlen statt Versprechen: Was Nestor im Alltag übernimmt, wo er heute an Grenzen stößt und wie sich das über die Zeit verändert.' },
    { slug: 'datenschutz-zuhause', img: 'assets/nestor/fenster.webp', date: '2026-05-12', read: '8 Min.', tag: 'Sicherheit', title: 'Datenschutz zuhause: Was Nestor sieht — und was nicht', teaser: 'Kameras in den eigenen vier Wänden sind Vertrauenssache. Wie wir Daten lokal verarbeiten, was das Haus nie verlässt und wie Sie jederzeit die Kontrolle behalten.' },
    { slug: 'vom-prototyp-zum-zuhause', img: 'assets/nestor/ansichten.webp', date: '2026-04-02', read: '10 Min.', tag: 'Hinter den Kulissen', title: 'Vom Prototyp zum Zuhause: Unser Weg zu Nestor 01', teaser: 'Sieben Jahre, viele Umwege und ein Grundsatz: Ein Roboter fürs Zuhause muss zuerst verlässlich sein, dann beeindruckend.' },
  ],

  team: [
    { name: 'Dr. Lena Hartmann', role: 'Gründerin & CEO', bio: 'Robotikerin, davor Assistenzsysteme in der Pflege. Überzeugt, dass Technik dem Menschen dienen muss — nicht umgekehrt.' },
    { name: 'Marcus Mustermann', role: 'Mitgründer & Head of Product', bio: 'Denkt vom Alltag her, nicht vom Datenblatt. Verantwortet, dass Nestor sich anfühlt wie ein guter Gast.' },
    { name: 'Priya Nair', role: 'Leitung KI & Sicherheit', bio: 'Baut die Systeme, die Nestor innehalten lassen, wenn er unsicher ist. Transparenz ist für sie kein Feature, sondern Pflicht.' },
    { name: 'Jonas Feldkamp', role: 'Leitung Fertigung', bio: 'Sorgt dafür, dass Nestor in Leipzig entsteht — reparierbar, langlebig und fair produziert.' },
  ],

  milestones: [
    { year: '2019', t: 'Gründung in Leipzig', d: 'Drei Menschen, eine Werkstatt und die Frage: Wie sähe ein Roboter aus, dem man das eigene Zuhause anvertraut?' },
    { year: '2022', t: 'Erster Prototyp „Nestor A0"', d: 'Kann einen Tisch decken. Braucht dafür 20 Minuten. Ein Anfang.' },
    { year: '2024', t: 'Pilothaushalte', d: '40 Familien leben ein Jahr lang mit einem Nestor. Wir lernen mehr über Alltag als in fünf Jahren Labor.' },
    { year: '2026', t: 'Nestor 01 wird ausgeliefert', d: 'Der erste, der wirklich hilft — in die ersten Zuhause.' },
  ],
};
